import { useGtm } from '@gtm-support/vue-gtm';
import liff from '@line/liff';

import { campaign } from '@/json/json_loader';
import { ActionBase } from '@/models/action_base';
import { KbnConst } from '@/utils/kbn';
import { redirectToLine } from '@/utils/redirect_to_line';

import { ActionLottery } from './use_action_lottery';
import { useApi } from './use_api';
import { useInsertDataStore } from './use_insert_data_store';
import { useLoader } from './use_loader';
import { useQueryStringStore } from './use_querystring_store';
import { useRum } from './use_rum';
import { useTrackingId } from './use_tracking_id';
import { useUser } from './use_user';

/**
 * カスタマイズアクション
 */
export class ActionCustomizing extends ActionBase {
  typeKbn = KbnConst.ACTION_TYPE_KBN_CUSTOMIZING;

  keyName?: string;

  use() {
    if (this.keyName === 'get_user') {
      const { getUser } = useUser();
      const loader = useLoader();
      return async () => {
        try {
          loader.start();
          await getUser();
        } finally {
          loader.stop();
        }
        return false;
      };
    }
    if (this.keyName === 'send_message') {
      const actionLottery = (new ActionLottery({
        insertType: 'sender_end',
      })).use();
      const { user } = useUser();
      const api = useApi();
      const loader = useLoader();
      const rum = useRum();
      const trackingId = useTrackingId();

      return async () => {
        // シェア前に抽選し、抽選権を発行する
        const insertType = 'sender_start';
        let replacedText = '';
        try {
          loader.start();
          const responsePostInsert = await api.postInsert({ insertType });
          await api.getLottery(responsePostInsert.entryUuid);
          const entryPollingResponse = await api.getEntryPolling(responsePostInsert.entryUuid);
          if (entryPollingResponse && entryPollingResponse.responseCandidateUuid) {
            const responseCandidate = campaign.responseCandidates.find(x => x.uuid === entryPollingResponse.responseCandidateUuid);
            if (responseCandidate) {
              if (responseCandidate.pageName === 'error') {
                throw new Error('抽選結果がエラーでした。');
              }
            } else {
              throw new Error('遷移先ページが取得できませんでした。');
            }
          } else {
            throw new Error('結果が取得できませんでした。');
          }
          replacedText = campaign.shareMessageText.replace(/{{user_uuid}}/g, user.value?.userUuid || '').replace(/{{entry_uuid}}/g, responsePostInsert.entryUuid || '');
        } finally {
          loader.stop();
        }

        if (!liff.isApiAvailable('shareTargetPicker')) {
          // シェアターゲットピッカーが使用できない場合、RUMに記録する
          rum.recordEvent('isApiAvailable', {
            'tracking_id': trackingId,
          });
        }
        try {
          // LIFFのシェアターゲットピッカーを使用してメッセージを送信
          const res = await liff.shareTargetPicker([
            {
              'type': 'text',
              'text': replacedText,
            },
            {
              'type': 'video',
              'originalContentUrl': campaign.originalContentUrl,
              'previewImageUrl': campaign.previewImageUrl,
            },
          ], {
            isMultiple: false,
          });

          // シェアが成功したら抽選を実行
          if (res && res.status === 'success') {
            await actionLottery();
          }
          return true;
        } catch {
          rum.recordEvent('shareTargetPicker', {
            'tracking_id': trackingId,
          });
          throw new Error('シェアターゲットピッカー使用時にエラーが発生しました。');
        }
      };
    }
    if (this.keyName === 'set_user_uuid') {
      const querystringStore = useQueryStringStore();
      const { senderUserUuid, senderEntryUuid } = useInsertDataStore();
      return async () => {
        senderUserUuid.value = querystringStore.getValues().u;
        senderEntryUuid.value = querystringStore.getValues().e;
        return false;
      };
    }
    if (this.keyName === 'redirect_to_line') {
      return async () => {
        redirectToLine(campaign.clientBasicId, '');
        return true;
      };
    }

    //カスタムアクション名に続く文字をeventとして設定する
    const prefix = 'gtm_click_event_';
    if (this.keyName?.startsWith(prefix)) {
      const gtm = useGtm();
      return async () => {
        gtm?.trackEvent({
          event: this.keyName?.slice(prefix.length),
          category: 'Click',
          noninteraction: true,
        });
        return false;
      };
    }
    return async () => await execute(this.keyName);
  }

  constructor(init: Partial<ActionCustomizing> = {}) {
    super();
    Object.assign(this, init);
  }
}

async function execute(
  keyName: string | undefined,
) {
  // キー名を使って好きな処理を実行できる
  alert(`このアクションは未実装（キー名: ${ keyName }）`);
  return false;
}

export function generateActionCustomizing(keyName?: string): ActionCustomizing {
  return new ActionCustomizing({
    keyName,
  });
}
